import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  REGISTER_REPORT_SECTIONS, DEFAULT_REGISTER_REPORT_SECTIONS, RegisterReportSection, normalizeReportSections,
} from '../../utils/registerReportSections';
import UnsavedChangesBar from './UnsavedChangesBar';
import { SettingsSheet, SheetBlock, SheetRow, SheetSection } from './sheet';
import { SettingsSwitch, StoreLoadState, StoreRow, useStoreSettingsForm } from './storeSettingsForm';

/**
 * Ταμείο → «Ζ και Χ» (12/10/2026, από την παλιά SettingsPage): ποιες ενότητες τυπώνει το κλείσιμο
 * ταμείου (Ζ) και η αναφορά Χ — stores.register_report_sections (migration 253), ίδιες και για τα δύο,
 * προεπιλογή μόνο το κλασικό. Τυπώνει ο server με τη ρύθμιση της βάσης (utils/registerStaffReport.js)·
 * αφορά ΜΟΝΟ το χαρτί — η οθόνη «Ταμείο» δείχνει πάντα όλες τις καρτέλες. Μία τουλάχιστον (ο server
 * απαντά 400 INVALID_REGISTER_REPORT_SECTIONS σε κενή λίστα), πάντα στη σταθερή σειρά.
 * Διάταξη «Φύλλο» (13/10/2026): μία ενότητα, ένας διακόπτης ανά ενότητα του Ζ/Χ.
 * «Κινήσεις ταμείου» (19/10/2026, migration 277): stores.cash_movements_enabled — έξοδα / αναλήψεις /
 * καταθέσεις από το «Ταμείο» και «Μετρητά στο συρτάρι» στο Ζ/Χ. Κλειστό εκ προεπιλογής· πύλη και στον server.
 */

// Ετικέτα και περιγραφή κάθε ενότητας (κλειδιά settings:page.*).
const SECTION_KEYS: Record<RegisterReportSection, { label: string; hint: string }> = {
  classic: { label: 'registerReportClassicLabel', hint: 'registerReportClassicHint' },
  users: { label: 'registerReportUsersLabel', hint: 'registerReportUsersHint' },
  staff_actions: { label: 'registerReportStaffActionsLabel', hint: 'registerReportStaffActionsHint' },
};

interface ReportsForm {
  register_report_sections: RegisterReportSection[];
  cash_movements_enabled: boolean;
}

const EMPTY: ReportsForm = { register_report_sections: [...DEFAULT_REGISTER_REPORT_SECTIONS], cash_movements_enabled: false };

const fromStore = (s: StoreRow): ReportsForm => ({
  register_report_sections: normalizeReportSections(s.register_report_sections),
  cash_movements_enabled: s.cash_movements_enabled === true,
});

const RegisterReportsPage: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { form, status, retry, saving, save } = useStoreSettingsForm({ guardId: 'store-settings:register-reports', empty: EMPTY, fromStore });
  const sections = form.values.register_report_sections;
  const savedSections = form.saved.register_report_sections;

  // Η τελευταία επιλεγμένη ενότητα δεν ξεεπιλέγεται.
  const toggle = (section: RegisterReportSection) => {
    form.setValues(prev => {
      const current = prev.register_report_sections;
      const has = current.includes(section);
      if (has && current.length <= 1) return prev;
      const next = has ? current.filter(s => s !== section) : current.concat(section);
      return { ...prev, register_report_sections: REGISTER_REPORT_SECTIONS.filter(s => next.includes(s)) };
    });
  };

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.register')} title={t('settings:registerReports.title')}
      subtitle={t('settings:registerReports.subtitle')} index={false}>
      {status !== 'ready' ? (
        <SheetBlock><StoreLoadState status={status} onRetry={retry} /></SheetBlock>
      ) : (
        <fieldset disabled={saving} className="min-w-0">
          <SheetSection id="register-report-sections" title={t('settings:registerReports.sectionsTitle')}
            hint={t('settings:page.registerReportSubtitle')} dirty={sections.join(',') !== savedSections.join(',')}>
            {REGISTER_REPORT_SECTIONS.map(section => {
              const checked = sections.includes(section);
              const locked = checked && sections.length === 1;
              return (
                <SheetRow
                  key={section}
                  labelId={`register-report-${section}-label`}
                  title={t(`settings:page.${SECTION_KEYS[section].label}`)}
                  subtitle={t(`settings:page.${SECTION_KEYS[section].hint}`)}
                  changed={checked !== savedSections.includes(section)}
                  control={({ labelId, descriptionId }) => (
                    <SettingsSwitch checked={checked} disabled={locked} onChange={() => toggle(section)}
                      labelId={labelId} describedById={descriptionId} />
                  )}
                />
              );
            })}
            {sections.length === 1 && (
              <p data-testid="register-report-min-one" className="xs-note">
                {t('settings:page.registerReportMinOneHint')}
              </p>
            )}
            <p className="xs-note">{t('settings:page.registerReportApplyHint')}</p>
          </SheetSection>

          <SheetSection id="register-cash-movements" title={t('settings:registerReports.cashMovementsTitle')}
            hint={t('settings:registerReports.cashMovementsHint')} dirty={form.values.cash_movements_enabled !== form.saved.cash_movements_enabled}>
            <SheetRow
              labelId="register-cash-movements-label"
              title={t('settings:registerReports.cashMovementsLabel')}
              subtitle={t('settings:registerReports.cashMovementsDesc')}
              changed={form.values.cash_movements_enabled !== form.saved.cash_movements_enabled}
              control={({ labelId, descriptionId }) => (
                <SettingsSwitch checked={form.values.cash_movements_enabled}
                  onChange={() => form.setValues(prev => ({ ...prev, cash_movements_enabled: !prev.cash_movements_enabled }))}
                  labelId={labelId} describedById={descriptionId} />
              )}
            />
          </SheetSection>
        </fieldset>
      )}

      <UnsavedChangesBar dirty={form.dirty} saving={saving} onSave={() => save()} onReset={() => form.reset()} />
    </SettingsSheet>
  );
};

export default RegisterReportsPage;
